import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Typography,
  Box,
  ThemeProvider,
  Card,
  Dialog,
  IconButton,
  DialogTitle,
  Divider,
  DialogContent,
  CircularProgress,
  Button,
} from "@mui/material";
import { useTripContext } from "../../context/tripsContext";
import CardWithDialog from "../../components/cardWithModal/CardWithDialog";
import { theme } from "../../utils/theme/theme";
import TripDetailsCard from "./components/TripDetailsCard";
import { ApprovalStatus } from "../../types/FilterEnums";
import EditableLicensePlate from "./components/EditableLicensePlate";
import LoadCard from "./components/LoadCard";
import DatesCard from "./components/DatesCard";
import DocumentsCard from "./components/DocumentsCard";
import { tripDetailsPage } from "./style";
import { useBusinessProfile } from "../../utils/hooks/useBusinessProfile";
import NotesCard from "./components/NotesCard";
import OriginDestiCard from "./components/OriginDestiCard";
import ChipsDetails from "./components/ChipsDetails";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "../../utils/hooks/useWindowSize";
import ScrollableBox from "./components/ScrollableBox";
import TripTimeline from "./components/TripTimeline";
import { CloseRounded } from "@mui/icons-material";
import { useTripDocument } from "../../context/TripDocumentContext";
import { useAuth } from "../../utils/hooks/useAuth";
import useFleetInfo from "../../utils/hooks/useFleetInfo";
import { fleetService } from "../../api/fleetService/fleetServicesRegistered";
import { subDays } from "date-fns";

const TripDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const {
    tripDetails,
    tripAcceptance,
    error,
    fetchTripData,
    fetchLicensePlates,
    licensePlates,
    fetchStops,
    stops,
    tripEndpoints,
    notes,
    fetchNotes,
    fetchTripAcceptance,
    licensePlateLoader,
    clearTripDetails,
  } = useTripContext();

  const {
    businessProfile,
    legacyData,
    isLoaded,
    error: profileError,
  } = useBusinessProfile();
  const {
    termsAndConditions,
    getTermsAndConditions,
    termsCondLoader,
    termsCondError,
  } = useTripDocument();
  const { user } = useAuth();
  const { vehiclePlates, trailerPlates, loading } =
    useFleetInfo(businessProfile);
  const [waitingForPorfile, setWaitingForProfile] = useState(true);
  const { t } = useTranslation();
  const windowWidth = useWindowSize();
  const isMobile = useMemo(() => windowWidth < 700, [windowWidth]);
  const isMobileTimeline = useMemo(() => windowWidth < 1226, [windowWidth]);
  const navigate = useNavigate();
  const [terms, setTerms] = useState<{
    duvri: {
      name: string;
      description: string;
      documentBody: string | null;
      accepted: boolean;
    };
    generalTerms: {
      name: string;
      description: string;
      documentBody: string | null;
      accepted: boolean;
    };
    sop: {
      name: string;
      description: string;
      documentBody: string | null;
      accepted: boolean;
    };
  } | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const [loader, setLoader] = useState<boolean>(false);

  const [boxHeight, setBoxHeight] = useState(0);
  useEffect(() => {
    const updateBoxHeight = () => {
      const screenHeight = window.innerHeight;
      const calculatedHeight = screenHeight * 1; // 70% dell'altezza
      const limitedHeight = Math.min(Math.max(calculatedHeight, 300), 794); // Min 300px, Max 600px
      setBoxHeight(limitedHeight);
    };

    updateBoxHeight();
    window.addEventListener("resize", updateBoxHeight);

    return () => window.removeEventListener("resize", updateBoxHeight);
  }, []);

  useEffect(() => {
    if (!businessProfile) {
      return;
    }
    
    // Clear previous trip data when ID changes
    clearTripDetails();
    
    const fetchData = async () => {
      if (!businessProfile?.legacycodes || businessProfile.legacycodes.length === 0) {
        console.warn("No legacy codes found in business profile");
        setWaitingForProfile(true);
        return;
      }
      setWaitingForProfile(false);
      setLoader(true);

      try {
        const tripData = await fetchTripData(Number(id));

        if (tripData?.tripCreationDate) {
          const tripDate = new Date(tripData.tripCreationDate);
          const activationDate = businessProfile.agencyqualification
            ? new Date(businessProfile.agencyqualification)
            : null; // Valore di default

          if (activationDate && tripDate < subDays(activationDate, 7)) {
            navigate("/errorPage"); // Redirect alla pagina di cortesia
            return;
          }
        }

        // Recupera i dati principali
        await Promise.all([
          fetchTripAcceptance(Number(id), legacyData),
          fetchLicensePlates(Number(id)),
          fetchStops(Number(id)),
          fetchNotes(Number(id)),
        ]);

      } catch (error) {
        console.error("Errore nel recupero dati", error);
      } finally {
        setLoader(false);
      }
    };

    fetchData();
  }, [businessProfile]);

  // 🚀 Nuova useEffect separata per il fetch dell'autista
  useEffect(() => {
    if (!legacyData.length || !licensePlates.length || !tripEndpoints.length) {
      return;
    }

    const fetchDriver = async () => {
      try {
        setLoadingDriver(true);
        const legacyCode = legacyData[0].legacycode;
        const company = legacyData[0].company;
        const licensePlate =
          licensePlates.find((lp) => lp.equipmentType === "TRUCK")?.licensePlate || "";
        const tripId = id || "";
        const date = tripEndpoints[0].expectedArrival
          ? new Date(tripEndpoints[0].expectedArrival)
          : new Date();

        const data = await fleetService.getFirstDriver(
          legacyCode,
          company,
          licensePlate,
          date,
          tripId.toString()
        );
        setDriver(data);
      } catch (err) {
        setErrorDriver("Errore nel caricamento del driver.");
      } finally {
        setLoadingDriver(false);
      }
    };

    fetchDriver();
  }, [legacyData, licensePlates, tripEndpoints]);

  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);

  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language, currentLanguage]);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const termsPayload = {
          accountId: user ? user.accountId : "",
          contactId: user ? user.contactId : "",
          language: currentLanguage.toUpperCase(),
          includeAccepted: true
        };
        
        const response = await getTermsAndConditions(
          user ? user.accountId : "",
          user ? user.contactId : "",
          currentLanguage.toUpperCase(),
          true
        );
        setTerms(response);
      } catch (error) {
        console.error('Error fetching terms and conditions:', error);
      }
    };

    fetchTerms();
  }, [id, user, currentLanguage]);

  const [driver, setDriver] = useState<any | null>(null);
  const [loadingDriver, setLoadingDriver] = useState<boolean>(true);
  const [errorDriver, setErrorDriver] = useState<string | null>(null);

  if (error) {
    return (
      <Box sx={tripDetailsPage.errorBox}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!tripEndpoints || tripEndpoints.length === 0) {
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  console.log('tripDetails', tripDetails)
  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={isMobile ? tripDetailsPage.wrapper1M : tripDetailsPage.wrapper1D}
      >
        {tripDetails &&
          tripAcceptance &&
          tripAcceptance.approvalStatus === ApprovalStatus.TO_APPROVE && (
            <CardWithDialog tripDetails={tripDetails} />
          )}
        <Box
          sx={
            isMobile
              ? tripDetailsPage.headerWrapperM
              : tripDetailsPage.headerWrapperD
          }
        >
          {tripDetails && (
            <TripDetailsCard
              tripId={Number(id)}
              tripDetails={tripDetails}
              tripAcceptance={tripAcceptance}
            />
          )}

          <Box
            sx={
              isMobile
                ? tripDetailsPage.subheaderRightM
                : tripDetailsPage.subheaderRightD
            }
          >
            <Box
              sx={
                isMobile
                  ? tripDetailsPage.licensePlateM
                  : tripDetailsPage.licensePlateD
              }
            >
              {licensePlates && licensePlates.length > 0 ? (
                // Caso in cui ci siano dati
                <>
                  {/* Controlla se esiste un "TRUCK" e mostra il componente */}
                  {licensePlates.some(
                    (plate) => plate.equipmentType === "TRUCK"
                  ) ? (
                    licensePlates
                      .filter((plate) => plate.equipmentType === "TRUCK")
                      .map((plate, index) => (
                        <EditableLicensePlate
                          //isUpdatable={plate.isUpdatable}
                          isUpdatable={true}
                          status={tripDetails?.status || ""}
                          setDriver={setDriver}
                          setErrorDriver={setErrorDriver}
                          setLoadingDriver={setLoadingDriver}
                          tripEndpoints={tripEndpoints}
                          approvalStatus={tripAcceptance?.approvalStatus || "-"}
                          key={`truck-${index}`}
                          value={plate.licensePlate}
                          equipmentType={"TRUCK"}
                          tripId={id}
                          businessProfileName={legacyData}
                          onSave={(newValue) => {
                            // Inserire logica di salvataggio qui
                          }}
                          vehiclePlates={vehiclePlates}
                          trailerPlates={trailerPlates}
                        />
                      ))
                  ) : (
                    // Se non esiste "TRUCK", mostra quello di default
                    <EditableLicensePlate
                      
                      status={tripDetails?.status || ""}
                      setDriver={setDriver}
                      setErrorDriver={setErrorDriver}
                      setLoadingDriver={setLoadingDriver}
                      tripEndpoints={tripEndpoints}
                      approvalStatus={tripAcceptance?.approvalStatus || "-"}
                      key="default-truck"
                      value="-"
                      tripId={id}
                      equipmentType="TRUCK"
                      businessProfileName={legacyData}
                      onSave={(newValue) => {
                        // Inserire logica di salvataggio qui
                      }}
                      vehiclePlates={vehiclePlates}
                      trailerPlates={trailerPlates}
                    />
                  )}
                  {/* Controlla se esiste un "TRAILER" e mostra il componente */}
                  {licensePlates.some(
                    (plate) => plate.equipmentType === "TRAILER"
                  ) ? (
                    licensePlates
                      .filter((plate) => plate.equipmentType === "TRAILER")
                      .map((plate, index) => (
                        <EditableLicensePlate
                          //isUpdatable={plate.isUpdatable}
                          isUpdatable={true}
                          status={tripDetails?.status || ""}
                          setDriver={setDriver}
                          setErrorDriver={setErrorDriver}
                          setLoadingDriver={setLoadingDriver}
                          tripEndpoints={tripEndpoints}
                          approvalStatus={tripAcceptance?.approvalStatus || "-"}
                          tripId={id}
                          key={`trailer-${index}`}
                          value={plate.licensePlate}
                          equipmentType={"TRAILER"}
                          businessProfileName={legacyData}
                          vehiclePlates={vehiclePlates}
                          trailerPlates={trailerPlates}
                          onSave={(newValue) => {
                            // Inserire logica di salvataggio qui
                          }}
                        />
                      ))
                  ) : (
                    // Se non esiste "TRAILER", mostra quello di default
                    <EditableLicensePlate
                      status={tripDetails?.status || ""}
                      setDriver={setDriver}
                      setErrorDriver={setErrorDriver}
                      setLoadingDriver={setLoadingDriver}
                      tripEndpoints={tripEndpoints}
                      approvalStatus={tripAcceptance?.approvalStatus || "-"}
                      tripId={id}
                      key="default-trailer"
                      value="-"
                      equipmentType="TRAILER"
                      businessProfileName={legacyData}
                      onSave={(newValue) => {
                        // Inserire logica di salvataggio qui
                      }}
                      vehiclePlates={vehiclePlates}
                      trailerPlates={trailerPlates}
                    />
                  )}
                </>
              ) : (
                // Caso in cui `licensePlates` sia vuoto
                <>
                  <EditableLicensePlate
                    status={tripDetails?.status || ""}
                    setDriver={setDriver}
                    setErrorDriver={setErrorDriver}
                    setLoadingDriver={setLoadingDriver}
                    tripEndpoints={tripEndpoints}
                    approvalStatus={tripAcceptance?.approvalStatus || "-"}
                    tripId={id}
                    key="default-truck"
                    value="-"
                    equipmentType="TRUCK"
                    businessProfileName={legacyData}
                    onSave={(newValue) => {
                      // Inserire logica di salvataggio qui
                    }}
                    vehiclePlates={vehiclePlates}
                    trailerPlates={trailerPlates}
                  />
                  <EditableLicensePlate
                    
                    status={tripDetails?.status || ""}
                    setDriver={setDriver}
                    setErrorDriver={setErrorDriver}
                    setLoadingDriver={setLoadingDriver}
                    tripEndpoints={tripEndpoints}
                    approvalStatus={tripAcceptance?.approvalStatus || "-"}
                    tripId={id}
                    key="default-trailer"
                    value="-"
                    equipmentType="TRAILER"
                    businessProfileName={legacyData}
                    onSave={(newValue) => {
                      // Inserire logica di salvataggio qui
                    }}
                    vehiclePlates={vehiclePlates}
                    trailerPlates={trailerPlates}
                  />
                </>
              )}
            </Box>

            {isMobile ? (
              <div style={{ maxWidth: windowWidth, margin: "0 auto" }}>
                <ScrollableBox>
                  {tripDetails && (
                    <ChipsDetails
                      firstDriver={driver}
                      businessProfile={businessProfile}
                      tripDetails={tripDetails}
                    />
                  )}
                </ScrollableBox>
              </div>
            ) : (
              tripDetails && (
                <ChipsDetails
                  firstDriver={driver}
                  businessProfile={businessProfile}
                  tripDetails={tripDetails}
                />
              )
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={
          isMobileTimeline
            ? tripDetailsPage.horizontalWrapperM
            : tripDetailsPage.horizontalWrapperD
        }
      >
        <Box
          sx={
            isMobileTimeline
              ? tripDetailsPage.subWrapperM
              : tripDetailsPage.subWrapperD
          }
        >
          {tripEndpoints && id && legacyData && user && (
            <OriginDestiCard
              handleOpenDialog={handleOpenDialog}
              tripEndpoints={tripEndpoints}
              tripId={Number(id)}
              legacyData={legacyData}
              user={user}
            />
          )}

          <Box
            sx={
              isMobile
                ? tripDetailsPage.wrapperLoadDateM
                : tripDetailsPage.wrapperLoadDateD
            }
          >
            {tripDetails && <LoadCard tripDetails={tripDetails} />}
            {/*  {tripDetails &&  <DatesCard tripDetails={tripDetails} />} */}
          </Box>
          <NotesCard notes={notes || null} tripStops={stops || null} />
          <DocumentsCard terms={terms} tripId={id} />
        </Box>
        {isMobileTimeline ? (
          <Dialog
            fullScreen
            open={openDialog}
            onClose={handleCloseDialog}
            sx={{ padding: 0, margin: 0, zIndex: 10000 }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Trip Timeline
              </Typography>
              <IconButton
                onClick={handleCloseDialog}
                sx={{ alignSelf: "flex-end", margin: 1 }}
              >
                <CloseRounded />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ overflowX: "auto", padding: 0 }}>
              {stops && <TripTimeline tripStops={stops} notes={notes} />}
            </DialogContent>
          </Dialog>
        ) : (
          <Card
            sx={{
              display: "flex",
              width: "50%",
              margin: "16px 16px 16px 0",
              padding: "16px",
              overflowY: "auto",
              height: `${boxHeight}px`,
              overflowX: "auto",
            }}
          >
            {stops && <TripTimeline tripStops={stops} notes={notes} />}
          </Card>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default TripDetailsPage;
//test